import React, {useEffect, useState, useRef, useImperativeHandle, forwardRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {ActionSheet, Button, Card, List, Modal, NavBar, Toast} from 'antd-mobile';
import {getBottomNavBarContainerStyle } from "../../../../utils/deviceUtils";
import PickupPhoneAndNavigate, {PickupPhoneAndNavigateRef} from '../../components/PickupPhoneAndNavigate';
import {
    getBusinessDaysText,
    getPickupFailureReasonText,
    getUnitLabel,
    PickupCommonType,
    PickupFailureReason,
    pickupFailureReasons,
    UnitOptions
} from "../../../../enums/PickupEnum";
import {t} from "../../../../i18n";
import {pickupNavigationService} from "../../services/PickupNavigationService";
import {usePickupTaskAppointment, usePickupTaskOperations} from '../../store/pickupStore';
import {PickupAppointmentType} from "../../graphql/types/PickupTaskWithAppointmentsType";
import {buildAddressFromAppointment} from "../../../../utils/addressUtils";
import {formatTimestampToDateTime, formatTimestampByLanguage} from "../../../../utils/dateTimeUtils";
import {ExclamationCircleOutline} from 'antd-mobile-icons';
import { copyToClipboard } from '../../../../utils/copyToClipboard';
import {useTranslation} from "react-i18next";
import pickedUpDataService, {PickedUpData} from "../../../../services/PickedUpDataService";
import {PickUpPhotoType, PickUpPositionType} from "../../services/PickUpPhotoService";
import PickUpInfoItem from "../../components/PickUpInfoItem";
import {PickingUpPath} from "../../../../enums/PickUpNavigatePathEnum";

interface PickingUpMapModePageProps {
    appointmentId : string;
    status : string;
    fromMapMode : boolean;// 检查是否来自地图模式
    onClose?: () => void; // 可选的关闭回调
    onBeforeNavigate?: () => void; // 跳转前的回调，用于保存地图状态
}

export interface PickingUpDetailCardRef {
    closeLongPressModal: () => void;
}

const PickingUpDetailCard = forwardRef<PickingUpDetailCardRef, PickingUpMapModePageProps>(({
    appointmentId,
    status,
    fromMapMode,
    onClose,
    onBeforeNavigate,
}, ref) => {
    const navigate = useNavigate();
    // const location = useLocation();
    const hubFlag = status === 'start_point' || status === 'end_point';
    const pickupPhoneAndNavigateRef = useRef<PickupPhoneAndNavigateRef>(null);
    const { i18n } = useTranslation();
    const [failedActionSheetVisible, setFailedActionSheetVisible] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [successActionSheetVisible, setSuccessActionSheetVisible] = useState(false);

    // 复制地址功能
    const handleCopyAddress = async () => {
        if (!address || address.trim() === '') {
            Toast.show({
                content: t('pages.pickUp.pickingUp.noAddress'),
                position: 'bottom',
            });
            return;
        }

        try {
            const success = await copyToClipboard(address);
            if (success) {
                Toast.show({
                    content: t('pages.pickUp.pickingUp.addressCopied'),
                    position: 'bottom'
                });
            } else {
                Toast.show({
                    content: t('pages.pickUp.pickingUp.copyFailed'),
                    position: 'bottom'
                });
            }
        } catch (error) {
            console.error('Copy address failed:', error);
            Toast.show({
                content: t('pages.pickUp.pickingUp.copyFailed'),
                position: 'bottom'
            });
        }
    };
    const {pickupTaskAppointment,loading, refreshPickupTaskAppointmentList} = usePickupTaskAppointment(appointmentId || '');
    const [pickUpDetail, setPickUpDetail] = useState<PickupAppointmentType>();
    const [nextItem, setNextItem] = useState<PickupAppointmentType | null>(null);

    useEffect(() => {
        if (pickupTaskAppointment){
            setPickUpDetail(pickupTaskAppointment);
        }
    }, [pickupTaskAppointment]);

    // 取第一站会触发列表刷新，不能依赖 pickupTaskAppointment 对象，否则刷新产生新引用又触发本 effect，形成死循环
    useEffect(() => {
        if (status === 'end_point'){
            pickupNavigationService.getFirstPickupItem().then(setNextItem);
        }
    }, [status]);

    // 暴露关闭长按弹窗的方法给父组件
    useImperativeHandle(ref, () => ({
        closeLongPressModal: () => {
            pickupPhoneAndNavigateRef.current?.closeLongPressModal();
        }
    }));

    const handleDeliverySuccess = () => {
        // 显示揽收成功选择弹窗
        setSuccessActionSheetVisible(true);
    };

    // 处理揽收成功单位选择
    const handleUnitSelection = (unitValue: number) => {
        setSuccessActionSheetVisible(false);

        // 跳转前保存地图状态
        onBeforeNavigate?.();
        // 跳转到揽收成功页面，传递选择的单位信息和来源状态
        navigate(`/pickup/picked-up/${pickUpDetail?.id}/${unitValue}`, {
            state: { fromMapMode }
        });
        onClose?.(); // 关闭弹窗
    };

    // 交接包裹逻辑
    const handlePackageHandover = async () => {
        try {
            // 获取最新的列表数据
            const latestAppointmentList = await refreshPickupTaskAppointmentList();

            // 过滤掉已取消的数据
            const activeAppointments = latestAppointmentList.filter(
                (appointment: PickupAppointmentType) => appointment.status !== 'CANCELLED'
                    && appointment.status !== 'TRANSFER_OUT'
                    && appointment.status !== 'end_point'
                    && appointment.status !== 'start_point'
            );
            // 判断count是否大于1
            if (activeAppointments.length >= 1) {
                // 显示弹窗
                showIncompleteTaskModal();
            } else {
                // 跳转前保存地图状态
                onBeforeNavigate?.();
                // 直接跳转到交接包裹页面（终点交接）
                navigate('/pickup/package-handover', {
                    state: { fromMapMode }
                });
                onClose?.(); // 关闭弹窗
            }
        } catch (error) {
            console.error('Failed to fetch latest appointment list:', error);
            Toast.show({
                content: t('pages.pickUp.pickingUp.error.arrivedFailed'),
                position: 'center',
            });
        }
    };
    // 显示未完成任务弹窗
    const showIncompleteTaskModal = () => {
        const handler = Modal.show({
            header: (
                <ExclamationCircleOutline
                    style={{
                        fontSize: 64,
                        color: 'var(--adm-color-warning)',
                    }}
                />
            ),
            title: t('pages.pickUp.detail.handoverTitle'),
            content: (
                <div className="text-center">
                    {t('pages.pickUp.detail.handoverDesc')}
                </div>
            ),
            actions: [
                {
                    key: 'partial',
                    text: t('pages.pickUp.detail.handoverPartial'),
                    onClick: () => {
                        handler.close();
                        // 跳转前保存地图状态
                        onBeforeNavigate?.();
                        navigate('/pickup/package-handover',{
                            state:{
                                isPartialHandover: true,
                                fromMapMode
                            }
                        });
                        onClose?.(); // 关闭弹窗
                    },
                },
                {
                    key: 'continue',
                    text: t('pages.pickUp.detail.handoverContinue'),
                    primary: true,
                    onClick: () => {
                        handler.close();
                    },
                },
            ],
        });
    };
    const handleFailedActionSheetVisible = () => {
        setFailedActionSheetVisible(true);
    };
    const handleFailureReason = (reason: string) => {
        setFailedActionSheetVisible(false);
        // 跳转前保存地图状态
        onBeforeNavigate?.();
        navigate(`/pickup/failed/${pickUpDetail?.id}/${reason}`, {
            state: { fromMapMode }
        });
        onClose?.(); // 关闭弹窗
    };
    // 保存订单数据到本地
    const savePickedUpToLocal = async (): Promise<boolean> => {
        try {
            const pickedUpData: PickedUpData = {
                updatedAt: Date.now(),
                photos: [] as Array<{ type: PickUpPositionType; photo: PickUpPhotoType }>,
                id: pickUpDetail?.id?.toString() || '',
                type: 'arrivedAtStartingPoint',
                stopNumber: pickUpDetail?.sequence?.toString(),
                channelName: pickUpDetail?.channelName,
                taskId: pickUpDetail?.taskId || '0'
            };
            // 保存到本地
            const saved = await pickedUpDataService.savePickedUpData(pickedUpData);
            return saved;
        } catch (error) {
            console.error('Failed to save PickedUp to local:', error);
            return false;
        }
    };
    const { markArrivedAtStartingPoint } = usePickupTaskOperations();
    const handleArrived = async () => {
        setIsLoading(true);
        if (!pickUpDetail?.id) {
            Toast.show({
                content: t('pages.pickUp.pickingUp.error.noTaskId'),
                position: 'center',
            });
            return;
        }
        try {
            Toast.show({
                content: t('pages.pickUp.pickingUp.success.arrived'),
                position: 'center',
            });
            await markArrivedAtStartingPoint({
                appointmentId: pickUpDetail?.id ||  '',
                taskId: pickUpDetail?.taskId || '0',
            },false);

            onClose?.(); // 关闭弹窗
        } catch (error: any) {
            if (error.networkError) {
                await savePickedUpToLocal();
            }else {
                console.error('Failed to mark arrived at starting point:', error);
                Toast.show({
                    content: t('pages.pickUp.pickingUp.error.arrivedFailed'),
                    position: 'center',
                })
            }
        } finally {
            setIsLoading(false);
        }
    };
    const failureReasons:PickupFailureReason[] = pickupFailureReasons;
    const filteredFailureReasons = pickUpDetail?.hasLinkedPickupOrder
        ? failureReasons.filter(reason => reason.code !== 'ERPU010')
        : failureReasons;
    if (loading || !pickUpDetail) {
        return (
            <div className="p-4">
                <div className="flex-1 flex items-center justify-center">
                    <p>{loading ? t('pages.pickUp.pickingUp.loading') : t('pages.pickUp.pickingUp.noOrderInfo')}</p>
                </div>
            </div>
        );
    }
    const address = buildAddressFromAppointment(pickUpDetail);
    const successSelectOps:PickupCommonType[] = UnitOptions;
    const businessDaysStr = getBusinessDaysText(pickUpDetail.businessDays, i18n.language);
    return (
        <div className="flex flex-col h-full">
            {/* Order Details - 可滚动内容区域 */}
            <div className="flex-1 overflow-y-auto">
                <Card className="m-2 rounded-lg flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex flex-col w-14 h-full justify-center items-center">
                        <p className="text-2xl font-bold" style={{color: 'var(--adm-color-primary)'}}>
                            {status === 'start_point' && t('pages.pickUp.start')}
                            {status === 'end_point' && t('pages.pickUp.end')}
                            {!hubFlag && pickUpDetail?.sequence}
                        </p>
                    </div>
                    <div className="flex flex-col gap-2">
                        {!hubFlag &&
                            <>
                                <h3 className="text-lg font-medium">{pickUpDetail?.channelName}</h3>
                            </>
                        }
                        <h3 className="text-lg font-medium">{hubFlag ? pickUpDetail?.channelName : pickUpDetail?.contactName}</h3>
                        <div className="text-sm text-gray-500">{`${pickUpDetail?.contactPhone}${pickUpDetail?.ext ? `-${pickUpDetail?.ext}` : ''}`}</div>
                        <div className="flex-1 text-base">
                            <div className="flex items-center gap-2">
                                <span>{address}</span>
                                <button
                                    onClick={handleCopyAddress}
                                    className="p-1 flex items-center justify-center text-purple-700 "
                                    style={{ minWidth: '24px', height: '24px' }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M7 4V2C7 1.45 7.45 1 8 1H20C20.55 1 21 1.45 21 2V16C21 16.55 20.55 17 20 17H18V19C18 20.1 17.1 21 16 21H4C2.9 21 2 20.1 2 19V7C2 5.9 2.9 5 4 5H7V4M7 7H4V19H16V7H7Z"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <PickupPhoneAndNavigate ref={pickupPhoneAndNavigateRef} phone={pickUpDetail.contactPhone} address={address}/>

                <List
                    style={{
                        '--border-top': 'none',
                        '--border-bottom': 'none',
                        '--padding-left': '0px',
                        '--padding-right': '0px',
                    }}
                >
                    {hubFlag ? (
                        <PickUpInfoItem label={t('pages.pickUp.pickingUp.appointmentTime')}
                                        value={`${pickUpDetail.appointmentTime ? formatTimestampByLanguage(pickUpDetail.appointmentTime, i18n.language) : pickUpDetail.appointmentTime}`} />
                    ) : (
                        <>
                            <PickUpInfoItem label={t('pages.pickUp.pickingUp.appointmentNo')} value={pickUpDetail?.appointmentNumber || '-'} />
                            <PickUpInfoItem label={t('pages.pickUp.pickingUp.appointmentPickUp')}
                                            value={`${pickUpDetail?.quantity} ${getUnitLabel(Number(pickUpDetail?.unit), i18n.language)}`} />
                            <PickUpInfoItem label={t('pages.pickUp.pickingUp.businessHours')} value={businessDaysStr}/>
                            <div className="flex justify-between py-2 border-gray-100">
                                <span className="text-gray-600 text-sm"></span>
                                <span className="text-sm font-medium text-right">{pickUpDetail?.businessHours}</span>
                            </div>
                            <PickUpInfoItem label={t('pages.pickUp.pickingUp.appointmentPickUpDate')} value={pickUpDetail?.appointmentTime ? formatTimestampByLanguage(pickUpDetail.appointmentTime, i18n.language) : pickUpDetail?.appointmentTime} />
                            <PickUpInfoItem label={t('pages.pickUp.pickingUp.remark')} value={pickUpDetail?.remark || '-'} />
                        </>
                    )}
                </List>
                </Card>
            </div>

            {/* 固定底部按钮区域 */}
            <div className="flex-none px-4 py-2 gap-4 bg-white border-t border-gray-200">
                <div className="flex gap-4">
                {status === 'start_point' &&
                    <Button
                        color='primary'
                        onClick={handleArrived}
                        className="flex-1"
                        loading={ isLoading}
                    >
                        {t('pages.pickUp.finished.iHaveArrived')}
                    </Button>
                }
                {status === 'end_point' &&
                    <Button
                        color='primary'
                        onClick={handlePackageHandover}
                        className="flex-[1]"
                    >
                        {t('pages.pickUp.packageHandover.title')}
                    </Button>
                }
                {!hubFlag &&
                    <>
                        <Button
                            color='default'
                            onClick={handleFailedActionSheetVisible}
                            className="flex-[1]"
                            style={{
                                '--background-color': 'gray',
                                '--text-color': 'white'
                            }}
                        >
                            {t('pages.pickUp.pickUpFailed.title')}
                        </Button>
                        <Button
                            color='primary'
                            onClick={handleDeliverySuccess}
                            className="flex-[2]"
                        >
                            {t('pages.pickUp.pickedUp.title')}
                        </Button>
                    </>
                }
                </div>
            </div>



            <ActionSheet
                visible={successActionSheetVisible}
                extra={
                    <div className="text-gray-500">
                        <h3 className={`text-center`}>{t('pages.pickUp.detail.successSelectTitle')}</h3>
                        <span>{t('pages.pickUp.detail.successSelectDesc')}</span>
                    </div>
                }
                cancelText={t('pages.pickUp.detail.cancel')}
                onClose={() => setSuccessActionSheetVisible(false)}
                actions={successSelectOps.map(unit => ({
                    text: (
                        <div className="text-center py-2">
                            <div className="font-medium text-lg" style={{color: 'var(--adm-color-primary)'}}>
                                {getUnitLabel(unit.value, i18n.language)}
                            </div>
                        </div>
                    ),
                    key: unit.value,
                    onClick: () => handleUnitSelection(unit.value)
                }))}
            />

            <ActionSheet
                visible={failedActionSheetVisible}
                extra={
                    <div className="text-gray-500">
                        <h3 className={`text-center`}>{t('pages.pickUp.pickUpFailed.title')}</h3>
                        <span>{t('pages.pickUp.pickingUp.failedDesc')}</span>
                    </div>
                }
                cancelText={t('pages.delivery.detail.cancel')}
                onClose={() => setFailedActionSheetVisible(false)}
                actions={filteredFailureReasons.map(reason => ({
                    text: (
                        <div>
                            <div className="text-purple-700 font-medium text-lg">
                                {getPickupFailureReasonText(reason.code, i18n.language)}
                            </div>
                        </div>
                    ),
                    key: reason.code,
                    onClick: () => handleFailureReason(reason.code)
                }))}
            />
        </div>
    );
});

PickingUpDetailCard.displayName = 'PickingUpDetailCard';

export default PickingUpDetailCard;